/** 匠盟 DPI 卡片 — 独立导出供社区预览等场景复用 */
import React from "react";
import { useTranslation } from "react-i18next";
import { Iconfont, Slider, Switch } from "@eweadn-qiankun/shared-components";
import type { DpiProfile } from "../types";
import "../DpiCard.scss";

export interface JiangmengDpiCardProps {
  profile: DpiProfile;
  index: number;
  isActive: boolean;
  canDelete?: boolean;
  maxDpiValue?: number;
  minDpiValue?: number;
  dpiStep?: number;
  isOperating?: boolean;
  onSelect: (index: number) => void;
  onXValueChange: (index: number, value: number) => void;
  onXValueChangeComplete: (index: number, value: number) => void;
  onYValueChange: (index: number, value: number) => void;
  onYValueChangeComplete: (index: number, value: number) => void;
  onToggleXYIndependent: (index: number, independent: boolean) => void;
  onDelete: (index: number) => void;
  onColorClick: (index: number) => void;
  colorButtonRef: (el: HTMLElement | null) => void;
  /** 预览模式：禁止点击卡片切换选中态 */
  selectable?: boolean;
  /** 是否允许 X/Y 轴独立设置（YXT 等仅支持统一 DPI） */
  allowXyIndependent?: boolean;
}

export function JiangmengDpiCard({
  profile,
  index,
  isActive,
  canDelete = true,
  maxDpiValue = 42000,
  minDpiValue = 50,
  dpiStep = 50,
  isOperating = false,
  onSelect,
  onXValueChange,
  onXValueChangeComplete,
  onYValueChange,
  onYValueChangeComplete,
  onToggleXYIndependent,
  onDelete,
  onColorClick,
  colorButtonRef,
  selectable = true,
  allowXyIndependent = true,
}: JiangmengDpiCardProps): React.ReactElement {
  const { t } = useTranslation();
  const xyIndependent = allowXyIndependent && profile.xyIndependent;

  const colorTitle = `${t("mouse.dpi.currentColor")}: rgb(${profile.color.r},${profile.color.g},${profile.color.b})`;

  return (
    <div
      className={`dpi-card-wrapper${selectable ? "" : " dpi-card-wrapper--readonly"}`}
      onClick={
        selectable
          ? (e) => {
              const target = e.target as HTMLElement;
              if (
                target.closest("button, input, .el-slider-box, .switch") ||
                target.closest(".dpi-slider-wrap")
              ) {
                return;
              }
              onSelect(index);
            }
          : undefined
      }
    >
      <div className={`dpi-card ${isActive ? "active" : ""}`}>
        <div className="card-header">
          <div
            ref={colorButtonRef}
            className="card-title-block"
            style={{
              cursor: profile.enabled ? "pointer" : "not-allowed",
            }}
            title={colorTitle}
            onClick={(e) => {
              e.stopPropagation();
              if (profile.enabled) onColorClick(index);
            }}
          >
            <div
              className="color-button"
              style={{
                backgroundColor: `rgb(${profile.color.r},${profile.color.g},${profile.color.b})`,
              }}
            />
            <h3 className="card-title">DPI{index + 1}</h3>
          </div>

          <div className="card-header-right">
            {allowXyIndependent ? (
              <>
                <span className="toggle-label">
                  {t("mouse.dpi.xyIndependent")}
                </span>
                <Switch
                  checked={profile.xyIndependent}
                  onChange={(checked: boolean) =>
                    onToggleXYIndependent(index, checked)
                  }
                  disabled={!profile.enabled}
                />
              </>
            ) : null}
            {canDelete && (
              <button
                className="icon-btn"
                onClick={() => onDelete(index)}
                disabled={isOperating}
                title={t("mouse.dpi.deleteDpi")}
              >
                <Iconfont icon="delete" className="icon-btn" />
              </button>
            )}
          </div>
        </div>

        <div className="card-body">
          {!xyIndependent ? (
            <div className="slider-row combined">
              <label>
                {allowXyIndependent ? t("mouse.dpi.xyAxis") : "DPI"}
              </label>
              <div className="dpi-slider-wrap">
                <Slider
                  value={profile.xValue}
                  min={minDpiValue}
                  max={maxDpiValue}
                  step={dpiStep}
                  onChange={(v) => onXValueChange(index, v)}
                  onChangeComplete={(v) => onXValueChangeComplete(index, v)}
                  disabled={!profile.enabled}
                  showValue
                  valueFormatter={(v) => String(v)}
                  unit=""
                  className="dpi-slider"
                />
              </div>
            </div>
          ) : (
            <div className="slider-group separate">
              <div className="slider-row">
                <label>{t("mouse.dpi.xAxis")}</label>
                <div className="dpi-slider-wrap">
                  <Slider
                    value={profile.xValue}
                    min={minDpiValue}
                    max={maxDpiValue}
                    step={dpiStep}
                    onChange={(v) => onXValueChange(index, v)}
                    onChangeComplete={(v) => onXValueChangeComplete(index, v)}
                    disabled={!profile.enabled}
                    showValue
                    valueFormatter={(v) => String(v)}
                    unit=""
                    className="dpi-slider"
                  />
                </div>
              </div>
              <div className="slider-row">
                <label>{t("mouse.dpi.yAxis")}</label>
                <div className="dpi-slider-wrap">
                  <Slider
                    value={profile.yValue}
                    min={minDpiValue}
                    max={maxDpiValue}
                    step={dpiStep}
                    onChange={(v) => onYValueChange(index, v)}
                    onChangeComplete={(v) => onYValueChangeComplete(index, v)}
                    disabled={!profile.enabled}
                    showValue
                    valueFormatter={(v) => String(v)}
                    unit=""
                    className="dpi-slider"
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
